import React, { memo, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Slider,
  Box,
  Typography,
  LoadingButton,
  Icon,
  Autocomplete,
  TextField,
  Button,
  Switch,
  FormControlLabel,
} from "investira.react.components";
import services from "../../../services";
import { VALIDATIONS } from "../../../const";
import withResponseHandling from "../../../hoc/withResponseHandling";
import { RangeSlider } from "../../../components/molecules";

const EnquadramentoRegrasAddDialog = memo((props) => {
  const { open, onClose, data, readData, enquadramentoId } = props;

  const [hasAlvo, setHasAlvo] = useState(false);

  const storedRegras = useSelector(
    (store) => store.reports.enquadramento.regras.data
  );

  const regras = Object.values(storedRegras).map((xItem) => {
    return {
      ...xItem,
      label: `${xItem.enquadramento_secao?.descricao}`,
      tipo: `${xItem.enquadramento_secao?.descricao}`,
      criterio: xItem.criterio,
    };
  });

  const regrasId = data.map((xItem) => xItem.enquadramento_regra_id);

  const getMarks = (pLength = 11, pGap = 10) => {
    return Array.from({ length: pLength }, (_, xIndex) => {
      const xValue = xIndex * pGap;
      return {
        value: xValue,
        label: `${xValue}%`,
      };
    });
  };

  const getMarksAlvo = (pLength = 11, pMin = 0, pMax = 100) => {
    return Array.from({ length: pLength }, (_, xIndex) => {
      const xValue = Math.round(
        pMin + xIndex * ((pMax - pMin) / (pLength - 1))
      );
      return {
        value: xValue,
        label: `${xValue}%`,
      };
    });
  };

  const VALIDATION_SCHEMA = Yup.object().shape({
    enquadramento_regra_id: Yup.string().required(VALIDATIONS.REQUIRED),
    descricao: Yup.string().required(VALIDATIONS.REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      enquadramento_regra_id: "",
      alvo: null,
      minimo: 1,
      maximo: 100,
      descricao: "",
    },
    validationSchema: VALIDATION_SCHEMA,
    validateOnMount: true,
    onSubmit: (pValues, pActions) => {
      pActions.setSubmitting(true);
      services.enquadramentosRegras.add(
        {
          data: { enquadramento_id: enquadramentoId, ...pValues },
        },
        (rRes) => {
          readData({ enquadramento_id: rRes.data.enquadramento_id });
          onClose && onClose();
        },
        (rErr) => {
          props.responseErrorHandling(rErr);
        },
        () => {
          pActions.setSubmitting(false);
        }
      );
    },
  });

  function handleChangeRegra(_e, pValue) {
    formik.setFieldValue("enquadramento_regra_id", pValue?.id || "");
    formik.setFieldValue(
      "descricao",
      pValue?.enquadramento_resolucao.item || ""
    );
  }

  function handleChangeRange(pValue) {
    formik.setFieldValue("minimo", pValue[0]);
    formik.setFieldValue("maximo", pValue[1]);
  }

  useEffect(() => {
    if (hasAlvo && formik.values.alvo <= formik.values.minimo) {
      formik.setFieldValue("alvo", formik.values.minimo);
    }

    if (hasAlvo && formik.values.alvo >= formik.values.maximo) {
      formik.setFieldValue("alvo", formik.values.maximo);
    }

    if (!hasAlvo) {
      formik.setFieldValue("alvo", null);
    }
  }, [hasAlvo, formik.values]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={onClose}
      scroll="body"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>Adicionar Regra</DialogTitle>
      <DialogContent>
        <form noValidate onSubmit={formik.handleSubmit} autoComplete="off">
          <Stack gap={2} sx={{ my: 1 }}>
            <Autocomplete
              disablePortal
              options={regras.sort((a, b) => -b.tipo.localeCompare(a.tipo))}
              groupBy={(option) => option.tipo}
              getOptionDisabled={(option) => {
                return regrasId.includes(option.id);
              }}
              noOptionsText={"Nenhuma Regra Encontrada"}
              autoHighlight={true}
              onChange={handleChangeRegra}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={crypto.randomUUID()} {...optionProps}>
                    <Stack
                      direction="row"
                      flexGrow={1}
                      spacing={3}
                      alignItems="flex-start"
                    >
                      <Stack flexGrow={1}>
                        <Typography
                          variant="body2"
                          color="textPrimary"
                          sx={{ fontWeight: 600 }}
                        >
                          {option.id}
                        </Typography>
                        <Stack
                          //direction="row"
                          //spacing={{ xs: 0, sm: 1 }}
                          mb={1}
                        >
                          {option.criterio.map((xCriterio) => {
                            return (
                              <Typography
                                variant="caption"
                                color="textPrimary"
                                sx={{
                                  display: { xs: "none", sm: "inline-block" },
                                }}
                              >
                                {xCriterio}
                              </Typography>
                            );
                          })}
                        </Stack>
                      </Stack>
                    </Stack>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Regra"
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
            <Box>
              <Typography
                id="input-slider"
                variant="body2"
                color="textPrimary"
                gutterBottom
              >
                {`Minímo: ${formik.values.minimo}% - Máximo: ${formik.values.maximo}%`}
              </Typography>
              <RangeSlider
                sliderProps={{
                  valueLabelDisplay: "auto",
                  marks: getMarks(5, 25),
                }}
                onChange={handleChangeRange}
              />
            </Box>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={hasAlvo}
                    onChange={() => setHasAlvo(!hasAlvo)}
                    name="hasAlvo"
                  />
                }
                label="Habilitar Alvo"
              />
            </Box>
            {hasAlvo && (
              <Box>
                <Typography
                  id="input-slider"
                  variant="body2"
                  color="textPrimary"
                  gutterBottom
                >
                  Alvo: {`${formik.values.alvo}%`}
                </Typography>
                <Stack sx={{ mx: 2, my: 2 }}>
                  <Slider
                    label="Alvo"
                    name="alvo"
                    value={formik.values.alvo}
                    onChange={formik.handleChange}
                    track={false}
                    // max={range[1]}
                    // min={range[0]}
                    max={formik.values.maximo}
                    min={formik.values.minimo}
                    step={1}
                    valueLabelDisplay="auto"
                    // marks={getMarksAlvo(6, range[0], range[1])}
                    marks={getMarksAlvo(
                      6,
                      formik.values.minimo,
                      formik.values.maximo
                    )}
                  />
                </Stack>
              </Box>
            )}
            <Box>
              <TextField
                id="descricao"
                name="descricao"
                label="Descrição"
                value={formik.values.descricao}
                onChange={formik.handleChange}
                placeholder="Digite uma descrição"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                error={Boolean(formik.errors.descricao)}
                helperText={formik.errors.descricao}
                disabled={formik.isSubmitting}
                fullWidth
              />
            </Box>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button onClick={onClose}>Cancelar</Button>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<Icon color="inherit" iconName="insert" size={16} />}
              sx={{ px: 2 }}
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid}
              loading={formik.isSubmitting}
            >
              Adicionar
            </LoadingButton>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default withResponseHandling(EnquadramentoRegrasAddDialog);
